import React from 'react';
import { LightbulbIcon, TargetIcon, FlameIcon, TrophyIcon, BrainIcon, HomeIcon } from 'lucide-react';
import type { DjayEmotion } from './DjayAvatar';
import type { BubbleColorScheme } from './DjayBubble';
import type { LearnerContext } from './djayMessages';
/**
 * Contextual Menu Builder — Djay's "What shall we do?" panel.
 *
 * Extracted from FloatingDjay.tsx (audit P1, ronde 2 2026) so the
 * 1300-line floating component focuses purely on interaction state, not
 * the static catalog of menu items.
 *
 * Pure function — receives current view + learner context + handler
 * callbacks, returns the items array. No React state, no hooks.
 *
 * Ronde 7: extracted `makeOefenenItem` factory so the four context-aware
 * "Ga oefenen" buttons share one source of truth for styling/label/icon.
 */
export type ItemAccessory = 'book' | 'sparkles' | 'none';
export interface PanelMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  hoverClass: string;
  textClass: string;
  onClick: () => void;
  /** Hint Djay speaks when this item is hovered/focused — first-person, friendly */
  bubbleHint: string;
  /** Color scheme for the speech bubble when this item is hovered */
  bubbleColor: BubbleColorScheme;
  /** Emotion Djay shows when this item is hovered/focused */
  hoverEmotion: DjayEmotion;
  /** Optional accessory prop that floats next to Djay when this item is focused */
  accessory?: ItemAccessory;
}
export interface MenuHandlers {
  onTipClick: () => void;
  onNavigate: (path: string) => void;
  onErrorTipClick: (tip: string) => void;
}
export interface MenuErrorPattern {
  recommendation: string;
  familyName: string;
}
/**
 * Factory for the "Ga oefenen" menu item — used across home/progress/
 * collection/default views. Only the bubble hint differs per context.
 */
function makeOefenenItem(bubbleHint: string, onNavigate: (path: string) => void): PanelMenuItem {
  return {
    id: 'oefenen',
    label: 'Ga oefenen',
    icon: <TargetIcon className="w-4 h-4" data-id="element-4381" data-mp="2c86ub76dwa8j" />,
    bgClass: 'bg-orange-50',
    hoverClass: 'hover:bg-orange-100',
    textClass: 'text-orange-700',
    onClick: () => onNavigate('learningLab'),
    bubbleHint,
    bubbleColor: 'default',
    hoverEmotion: 'excited',
    accessory: 'sparkles'
  };
}
export function getContextualMenuItems(currentView: string, learnerContext: LearnerContext, errorPatterns: MenuErrorPattern[], handlers: MenuHandlers): PanelMenuItem[] {
  const {
    streak,
    displayName
  } = learnerContext;
  const items: PanelMenuItem[] = [];

  // ── Volgorde ───────────────────────────────────────────────────────
  //
  // Het menu opende eerder met "Weetje van de dag" — het minst nuttige item
  // stond bovenaan, en de actie waar het kind voor kwam eronder. Voor een
  // kind van 6-12 is het eerste item in de praktijk hét item: verder kijken
  // gebeurt nauwelijks.
  //
  // De volgorde is nu: doen → leren → weten → weg. Eerst wat je nú kunt doen
  // op dit scherm, dan de persoonlijke tip, dan het weetje, en pas onderaan de
  // dingen die je wegnavigeren.

  // 1. De actie die bij dit scherm hoort.
  switch (currentView) {
    case 'home':
      items.push(makeOefenenItem(`Klaar om te trainen, ${displayName}? Ik heb een plan! 💪`, handlers.onNavigate),
      // Op de startpagina was "Ga oefenen" de enige keuze. Een kind dat
      // liever zélf een vak kiest dan het voorgestelde plan volgt, had geen
      // tweede weg — dat is precies het moment waarop een menu iets hoort
      // toe te voegen.
      {
        id: 'kies_vak',
        label: 'Kies zelf een vak',
        icon: <BrainIcon className="w-4 h-4" data-id="element-4382" data-mp="y8lw43gy460k" />,
        bgClass: 'bg-sky-50',
        hoverClass: 'hover:bg-sky-100',
        textClass: 'text-sky-700',
        onClick: () => handlers.onNavigate('modules'),
        bubbleHint: 'Liever zelf kiezen? Ik laat je alle vakken zien! 📚',
        bubbleColor: 'default',
        hoverEmotion: 'pointing',
        accessory: 'book'
      });
      break;
    case 'subjects':
    case 'modules':
      items.push({
        id: 'voortgang',
        label: 'Bekijk voortgang',
        icon: <BrainIcon className="w-4 h-4" data-id="element-4383" data-mp="j76n82s9646h" />,
        bgClass: 'bg-emerald-50',
        hoverClass: 'hover:bg-emerald-100',
        textClass: 'text-emerald-700',
        onClick: () => handlers.onNavigate('progress'),
        bubbleHint: 'Kijk hoe ver je al gekomen bent! Ik ben trots! 🌟',
        bubbleColor: 'emerald',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    case 'progress':
      items.push(makeOefenenItem('Tijd om die voortgang te laten groeien! 🚀', handlers.onNavigate));
      break;
    case 'collection':
      items.push(makeOefenenItem('Verdien meer trofeeën — ga oefenen! 🏆', handlers.onNavigate));
      break;
    case 'learningLab':
    case 'lab':
      items.push({
        id: 'collectie',
        label: 'Mijn collectie',
        icon: <TrophyIcon className="w-4 h-4" data-id="element-4384" data-mp="1kwsgerqk9l7w" />,
        bgClass: 'bg-violet-50',
        hoverClass: 'hover:bg-violet-100',
        textClass: 'text-violet-700',
        onClick: () => handlers.onNavigate('collection'),
        bubbleHint: 'Bekijk alle trofeeën die je verdiend hebt! ✨',
        bubbleColor: 'violet',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    default:
      items.push(makeOefenenItem('Kom, we gaan oefenen! 💪', handlers.onNavigate));
      break;
  }

  // 2. De persoonlijke tip. Stond eerst bovenaan, maar hij is reactief — hij
  //    gaat over iets dat al gebeurd is. De actie voor nú komt eerst.
  if (errorPatterns.length > 0) {
    const topPattern = errorPatterns[0];
    items.push({
      id: 'error_tip',
      // Het onderwerp staat nu in het label zelf. "Tip voor jou" dwong het
      // kind om te hoveren of te klikken om te ontdekken waar de tip over
      // ging — op een aanraakscherm bestaat hoveren niet, dus daar was het
      // een dichte deur.
      label: `Tip over ${topPattern.familyName.toLowerCase()}`,
      icon: <LightbulbIcon className="w-4 h-4" data-id="element-4385" data-mp="ptfwftqcqgry" />,
      bgClass: 'bg-violet-50',
      hoverClass: 'hover:bg-violet-100',
      textClass: 'text-violet-700',
      onClick: () => handlers.onErrorTipClick(topPattern.recommendation),
      bubbleHint: `Ik zag iets bij ${topPattern.familyName.toLowerCase()} — wil je m'n tip horen? ✨`,
      bubbleColor: 'violet',
      hoverEmotion: 'encouraging',
      accessory: 'sparkles'
    });
  }

  // 3. Het weetje. Leuk, maar niet waar iemand het menu voor opent.
  items.push({
    id: 'weetje',
    label: 'Weetje van de dag',
    icon: <LightbulbIcon className="w-4 h-4" data-id="element-4386" data-mp="3t9mwzrdgzxwi" />,
    bgClass: 'bg-amber-50',
    hoverClass: 'hover:bg-amber-100',
    textClass: 'text-amber-700',
    onClick: handlers.onTipClick,
    bubbleHint: 'Ik heb elke dag een nieuw weetje voor je! Klik maar 🤓',
    bubbleColor: 'amber',
    hoverEmotion: 'pointing',
    accessory: 'book'
  });

  // 4. De reeks. Het label was `Mijn streak: 5 dagen` — een getal dat eruitzag
  //    als een mededeling, terwijl het een knop naar de voortgangspagina was.
  //    Een kind dat erop tikte werd verrast met een navigatie die het niet
  //    vroeg. Het label zegt nu wat de knop dóét; het getal staat erachter.
  //
  //    Niet tonen op de voortgangspagina zelf: een knop die je brengt waar je
  //    al bent, is de duidelijkste manier om een menu waardeloos te laten
  //    voelen.
  if (streak > 0 && currentView !== 'progress') {
    items.push({
      id: 'streak',
      label: `Bekijk je reeks (${streak} ${streak === 1 ? 'dag' : 'dagen'})`,
      icon: <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-4387" data-mp="dhwwqjctjg03" />,
      bgClass: 'bg-rose-50',
      hoverClass: 'hover:bg-rose-100',
      textClass: 'text-rose-700',
      onClick: () => handlers.onNavigate('progress'),
      bubbleHint: streak >= 7 ? `${streak} dagen op rij?! Je bent een legende! 🔥👑` : `${streak} dagen op rij — ga zo door! 🔥`,
      bubbleColor: 'amber',
      hoverEmotion: streak >= 7 ? 'celebrating' : 'excited',
      accessory: 'sparkles'
    });
  }

  // 5. Terug naar start. Ontbrak volledig, terwijl "hoe kom ik hier weg"
  //    precies de vraag is die een kind stelt als het ergens is beland waar
  //    het niet wilde zijn. Onderaan, want het is een uitweg en geen doel.
  if (currentView !== 'home') {
    items.push({
      id: 'naar_start',
      label: 'Terug naar start',
      icon: <HomeIcon className="w-4 h-4" data-id="element-4388" data-mp="15ix8iyj52iub" />,
      bgClass: 'bg-slate-50',
      hoverClass: 'hover:bg-slate-100',
      textClass: 'text-slate-700',
      onClick: () => handlers.onNavigate('home'),
      bubbleHint: 'Zal ik je terugbrengen naar het startscherm? 🏠',
      bubbleColor: 'default',
      hoverEmotion: 'happy',
      accessory: 'none'
    });
  }
  return items;
}